import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "setup";
import { UserGroupModel, UserModel } from "services/api/cplauthapi/CPLAuthAPI";
import { container } from "setup/dependency/inversify.config";
import { IUserGroupsService } from "services/UserGroupsService";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { IUsersService } from "services/UsersService";
import { toast } from "react-toastify";

type UserGroupMembersState = {
  UserGroup: UserGroupModel | undefined;
  UserGroupMembers: Array<UserModel> | undefined;
  Users: Array<UserModel> | undefined;
  LoadingUserGroupMembers: boolean;
  LoadingUsers: boolean;
  EditingUser: boolean;
  LoadingUserGroup: boolean;
};

const initialState: UserGroupMembersState = {
  UserGroup: new UserGroupModel(),
  UserGroupMembers: new Array<UserModel>(),
  Users: new Array<UserModel>(),
  LoadingUserGroupMembers: false,
  LoadingUsers: false,
  EditingUser: false,
  LoadingUserGroup: false,
};

export const userGroupMemberSlice = createSlice({
  name: "userGroupMember",
  initialState,
  reducers: {
    resetGroupMembers: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserGroupMembers.pending, (state) => {
        state.LoadingUserGroupMembers = true;
      })
      .addCase(fetchUserGroupMembers.rejected, (state) => {
        toast.error("An unexpected error occurred");
        state.LoadingUserGroupMembers = false;
      })
      .addCase(fetchUserGroupMembers.fulfilled, (state, action) => {
        state.UserGroupMembers = action.payload;
        state.LoadingUserGroupMembers = false;
      })
      .addCase(addUserToGroup.pending, (state) => {
        state.EditingUser = true;
      })
      .addCase(addUserToGroup.rejected, (state) => {
        toast.error("An unexpected error occurred");
        state.EditingUser = false;
      })
      .addCase(addUserToGroup.fulfilled, (state) => {
        toast.success("User Added");
        state.EditingUser = false;
      })
      .addCase(fetchUserGroup.pending, (state) => {
        state.LoadingUserGroup = true;
      })
      .addCase(fetchUserGroup.rejected, (state) => {
        toast.error("An unexpected error occurred");
        state.LoadingUserGroup = false;
      })
      .addCase(fetchUserGroup.fulfilled, (state, action) => {
        state.UserGroup = action.payload;
        state.LoadingUserGroup = false;
      })
      .addCase(fetchAllUsers.pending, (state) => {
        state.LoadingUsers = true;
      })
      .addCase(fetchAllUsers.rejected, (state) => {
        toast.error("An unexpected error occurred");
        state.LoadingUsers = false;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.Users = action.payload;
        state.LoadingUsers = false;
      })
      .addCase(deleteUserFromGroup.pending, (state) => {
        state.EditingUser = true;
      })
      .addCase(deleteUserFromGroup.rejected, (state) => {
        toast.error("An unexpected error occurred");
        state.EditingUser = false;
      })
      .addCase(deleteUserFromGroup.fulfilled, (state) => {
        toast.warning("User removed from the group");
        state.EditingUser = false;
      });
  },
});

export const fetchUserGroupMembers = createAsyncThunk(
  "UserGroupsMembers/FetchUserGroupMembers",
  async (groupID: number) => {
    const usersGroupService = container.get<IUserGroupsService>(SERVICE_TYPES.UserGroupsService);
    return await usersGroupService.getUserGroupMembers(groupID);
  }
);

export const deleteUserFromGroup = createAsyncThunk<
  void,
  { userID: string; userGroupID: number },
  { state: RootState }
>("UserGroupsMembers/DeleteUserFromGroup", async ({ userID, userGroupID }, thunkAPI) => {
  const usersGroupService = container.get<IUserGroupsService>(SERVICE_TYPES.UserGroupsService);
  await usersGroupService.deleteUserFromGroup(userID, userGroupID);
  thunkAPI.dispatch(fetchUserGroupMembers(userGroupID));
});

export const addUserToGroup = createAsyncThunk<void, { userID: string; userGroupID: number }, { state: RootState }>(
  "UserGroupsMembers/AddUserToGroup",
  async ({ userID, userGroupID }, thunkAPI) => {
    const usersGroupService = container.get<IUserGroupsService>(SERVICE_TYPES.UserGroupsService);
    const userResponse = await usersGroupService.addUserToGroup(userID, userGroupID);
    thunkAPI.dispatch(fetchUserGroupMembers(userGroupID));
    return userResponse;
  }
);

export const fetchUserGroup = createAsyncThunk("UserGroupMembers/FetchUserGroup", async (groupID: number) => {
  const usersGroupService = container.get<IUserGroupsService>(SERVICE_TYPES.UserGroupsService);
  return await usersGroupService.getUserGroup(groupID);
});

export const fetchAllUsers = createAsyncThunk("UserGroupMembers/FetchAllUsers", async () => {
  const usersService = container.get<IUsersService>(SERVICE_TYPES.UsersService);
  return await usersService.getAllUsers(false);
});

export const { resetGroupMembers } = userGroupMemberSlice.actions;

export const selectLoadingUserGroupMembers = (state: RootState) => {
  return state.userGroupMembers.LoadingUserGroupMembers;
};
export const selectLoadingUsers = (state: RootState) => {
  return state.userGroupMembers.LoadingUsers;
};
export const selectAddingUser = (state: RootState) => {
  return state.userGroupMembers.EditingUser;
};
export const selectLoadingUserGroup = (state: RootState) => {
  return state.userGroupMembers.LoadingUserGroup;
};

export const selectUserGroup = (state: RootState) => {
  return state.userGroupMembers.UserGroup;
};
export const selectUserGroupMembers = (state: RootState) => {
  return state.userGroupMembers.UserGroupMembers;
};
export const selectFilteredUsers = (state: RootState) => {
  if (state.userGroupMembers?.Users == undefined) return null;
  const filtered = state.userGroupMembers.Users.filter((element) => {
    return (state.userGroupMembers.UserGroupMembers?.map((i) => i.userID).indexOf(element.userID) ?? 0) < 0;
  });
  return filtered.map((i) => ({
    value: i.userID!,
    label: i.emailAddress!,
  }));
};

export default userGroupMemberSlice.reducer;
